<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

const show = defineModel('show', { type: Boolean, default: false });

const getters = useStoreGetters();
const { t } = useI18n();

const availableInboxes = computed(() =>
  getters['inboxes/getInboxes'].value.filter(inbox =>
    ['Channel::Api', 'Channel::Whatsapp', 'Channel::Email'].includes(
      inbox.channel_type
    )
  )
);

const fromInboxId = ref('');
const toInboxId = ref('');
const isFromOpen = ref(false);
const isToOpen = ref(false);
const isMigrating = ref(false);
const isCompleted = ref(false);
const taskId = ref(null);
const percent = ref(0);
// Visual smoothing for progress updates
const animatedPercent = ref(0); // what we render
const targetPercent = ref(0); // where animation heads
const status = ref('queued');
let poller = null;
let ticker = null;
const MIN_VISUAL_DURATION_MS = 1200;
let startedAt = 0;
const completePending = ref(false);
const totals = ref({
  total_contact_inboxes: 0,
  processed_contact_inboxes: 0,
  total_conversations: 0,
  processed_conversations: 0,
  total_messages: 0,
  processed_messages: 0,
  total_captain_inboxes: 0,
  processed_captain_inboxes: 0,
});

const targetInboxes = computed(() => {
  const sourceInbox = availableInboxes.value.find(
    inbox => inbox.id === fromInboxId.value
  );
  // If migrating from Email, force target to Email as well
  if (sourceInbox?.channel_type === 'Channel::Email') {
    return availableInboxes.value.filter(
      inbox => inbox.channel_type === 'Channel::Email'
    );
  }
  // For WhatsApp targets, only allow Evolution provider for now; always exclude same inbox id
  return availableInboxes.value.filter(inbox => {
    if (inbox.id === fromInboxId.value) return false;
    if (inbox.channel_type !== 'Channel::Whatsapp') return true;
    return inbox.provider === 'evolution';
  });
});

// Ensure target never equals source; clear invalid selection on change
watch(
  () => fromInboxId.value,
  () => {
    const validIds = new Set(targetInboxes.value.map(i => i.id));
    if (
      !validIds.has(toInboxId.value) ||
      toInboxId.value === fromInboxId.value
    ) {
      toInboxId.value = '';
    }
  }
);

function stopPolling() {
  if (poller) {
    clearInterval(poller);
    poller = null;
  }
}

function stopTicker() {
  if (ticker) {
    clearInterval(ticker);
    ticker = null;
  }
}

function finalizeIfReady() {
  if (
    completePending.value &&
    Math.round(animatedPercent.value) >= Math.round(targetPercent.value) &&
    Date.now() - startedAt >= MIN_VISUAL_DURATION_MS
  ) {
    stopTicker();
    isMigrating.value = false;
    isCompleted.value = true;
    useAlert(t('INBOX_MGMT.MIGRATE.API.SUCCESS_MESSAGE'));
  }
}

function startTicker() {
  stopTicker();
  ticker = setInterval(() => {
    const current = animatedPercent.value;
    const target = Math.max(0, Math.min(100, targetPercent.value));
    if (current >= target) {
      finalizeIfReady();
      return;
    }
    const delta = target - current;
    const step = Math.max(1, Math.ceil(delta / 5)); // ~0.5–1s ease
    animatedPercent.value = Math.min(100, current + step);
    finalizeIfReady();
  }, 100);
}

function startPolling() {
  stopPolling();
  poller = setInterval(async () => {
    try {
      const { data } = await InboxesAPI.migrationStatus(taskId.value);
      // Persist counts for optional display or fallback percent
      totals.value = {
        total_contact_inboxes: data.total_contact_inboxes ?? 0,
        processed_contact_inboxes: data.processed_contact_inboxes ?? 0,
        total_conversations: data.total_conversations ?? 0,
        processed_conversations: data.processed_conversations ?? 0,
        total_messages: data.total_messages ?? 0,
        processed_messages: data.processed_messages ?? 0,
        total_captain_inboxes: data.total_captain_inboxes ?? 0,
        processed_captain_inboxes: data.processed_captain_inboxes ?? 0,
      };

      // Use server percent if present; else compute fallback
      let p = Number(data.percent);
      if (!Number.isFinite(p)) {
        const total =
          totals.value.total_contact_inboxes +
          totals.value.total_conversations +
          totals.value.total_messages +
          totals.value.total_captain_inboxes;
        const processed =
          totals.value.processed_contact_inboxes +
          totals.value.processed_conversations +
          totals.value.processed_messages +
          totals.value.processed_captain_inboxes;
        p = total > 0 ? Math.round((processed * 100) / total) : 0;
      }
      percent.value = Math.max(0, Math.min(100, p));
      targetPercent.value = percent.value;
      status.value = data.status;
      if (status.value === 'completed') {
        stopPolling();
        percent.value = 100;
        targetPercent.value = 100;
        completePending.value = true;
      }
      if (status.value === 'failed') {
        stopPolling();
        stopTicker();
        isMigrating.value = false;
        isCompleted.value = false;
        useAlert(t('INBOX_MGMT.MIGRATE.API.ERROR_MESSAGE'));
      }
    } catch (e) {
      // stop polling on any unexpected error
      stopPolling();
      stopTicker();
      isMigrating.value = false;
      useAlert(t('INBOX_MGMT.MIGRATE.API.ERROR_MESSAGE'));
    }
  }, 1000);
}

const resetState = () => {
  stopPolling();
  stopTicker();
  fromInboxId.value = '';
  toInboxId.value = '';
  isFromOpen.value = false;
  isToOpen.value = false;
  isMigrating.value = false;
  isCompleted.value = false;
  taskId.value = null;
  percent.value = 0;
  animatedPercent.value = 0;
  targetPercent.value = 0;
  status.value = 'queued';
  completePending.value = false;
  startedAt = 0;
  totals.value = {
    total_contact_inboxes: 0,
    processed_contact_inboxes: 0,
    total_conversations: 0,
    processed_conversations: 0,
    total_messages: 0,
    processed_messages: 0,
    total_captain_inboxes: 0,
    processed_captain_inboxes: 0,
  };
};

const closeModal = () => {
  resetState();
  show.value = false;
};

watch(
  () => show.value,
  val => {
    if (val) {
      // When opening the modal, ensure a clean state
      resetState();
    }
  }
);

const migrate = async () => {
  try {
    const { data } = await InboxesAPI.migrate({
      from_inbox_id: fromInboxId.value,
      to_inbox_id: toInboxId.value,
    });
    taskId.value = data.task_id;
    isMigrating.value = true;
    isCompleted.value = false;
    startedAt = Date.now();
    animatedPercent.value = 0;
    targetPercent.value = 5; // kick off a tiny visual head start
    completePending.value = false;
    status.value = data.status;
    startTicker();
    startPolling();
  } catch (error) {
    useAlert(t('INBOX_MGMT.MIGRATE.API.ERROR_MESSAGE'));
  }
};

// Helpers to handle selection without inline expression churn
const selectFrom = inbox => {
  fromInboxId.value = inbox.id;
  isFromOpen.value = false;
};
const selectTo = inbox => {
  toInboxId.value = inbox.id;
  isToOpen.value = false;
};

onBeforeUnmount(() => stopPolling());
onBeforeUnmount(() => stopTicker());

const selectedFromInbox = computed(() =>
  availableInboxes.value.find(inbox => inbox.id === fromInboxId.value)
);
const selectedToInbox = computed(() =>
  targetInboxes.value.find(inbox => inbox.id === toInboxId.value)
);
</script>

<template>
  <Modal v-model:show="show" :on-close="closeModal">
    <woot-modal-header :header-title="t('INBOX_MGMT.MIGRATE.TITLE')" />
    <div class="flex flex-col gap-5 p-8">
      <div v-if="!isMigrating && !isCompleted" class="flex flex-col gap-4">
        <div>
          <label class="block mb-1 text-xs font-medium text-n-slate-11">
            {{ t('INBOX_MGMT.MIGRATE.FROM') }}
          </label>
          <div v-on-click-outside="() => (isFromOpen = false)" class="relative">
            <button
              type="button"
              class="w-full h-9 flex items-center justify-between gap-2 rounded-md outline outline-1 outline-n-weak bg-n-solid-2 hover:bg-n-slate-3 px-3 text-left text-sm focus:outline-none focus:ring ring-n-weak transition"
              @click="isFromOpen = !isFromOpen"
            >
              <span class="flex items-center gap-2 truncate">
                <ChannelIcon
                  v-if="selectedFromInbox"
                  :inbox="selectedFromInbox"
                  class="size-4 text-n-slate-10"
                />
                <span class="truncate text-n-slate-12">
                  {{
                    selectedFromInbox?.name ||
                    t('INBOX_MGMT.MIGRATE.SELECT_INBOX')
                  }}
                </span>
              </span>
              <i class="i-lucide-chevron-down size-4 text-n-slate-10" />
            </button>
            <div v-if="isFromOpen" class="absolute mt-1 left-0 w-full z-50">
              <ul
                class="w-full max-h-64 overflow-auto bg-n-solid-2 outline outline-1 outline-n-weak rounded-md shadow-lg p-1"
              >
                <li v-for="inbox in availableInboxes" :key="inbox.id">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-n-alpha-2"
                    @click="selectFrom(inbox)"
                  >
                    <span class="flex items-center gap-2 min-w-0">
                      <ChannelIcon
                        :inbox="inbox"
                        class="size-4 text-n-slate-10"
                      />
                      <span class="truncate">{{ inbox.name }}</span>
                    </span>
                    <i
                      v-if="fromInboxId === inbox.id"
                      class="i-lucide-check size-4 text-n-slate-10"
                    />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div>
          <label class="block mb-1 text-xs font-medium text-n-slate-11">
            {{ t('INBOX_MGMT.MIGRATE.TO') }}
          </label>
          <div v-on-click-outside="() => (isToOpen = false)" class="relative">
            <button
              type="button"
              class="w-full h-9 flex items-center justify-between gap-2 rounded-md outline outline-1 outline-n-weak bg-n-solid-2 hover:bg-n-slate-3 px-3 text-left text-sm focus:outline-none focus:ring ring-n-weak transition"
              @click="isToOpen = !isToOpen"
            >
              <span class="flex items-center gap-2 truncate">
                <ChannelIcon
                  v-if="selectedToInbox"
                  :inbox="selectedToInbox"
                  class="size-4 text-n-slate-10"
                />
                <span class="truncate text-n-slate-12">
                  {{
                    selectedToInbox?.name ||
                    t('INBOX_MGMT.MIGRATE.SELECT_INBOX')
                  }}
                </span>
              </span>
              <i class="i-lucide-chevron-down size-4 text-n-slate-10" />
            </button>
            <div v-if="isToOpen" class="absolute mt-1 left-0 w-full z-50">
              <ul
                class="w-full max-h-64 overflow-auto bg-n-solid-2 outline outline-1 outline-n-weak rounded-md shadow-lg p-1"
              >
                <li v-for="inbox in targetInboxes" :key="inbox.id">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-n-alpha-2"
                    @click="selectTo(inbox)"
                  >
                    <span class="flex items-center gap-2 min-w-0">
                      <ChannelIcon
                        :inbox="inbox"
                        class="size-4 text-n-slate-10"
                      />
                      <span class="truncate">{{ inbox.name }}</span>
                    </span>
                    <i
                      v-if="toInboxId === inbox.id"
                      class="i-lucide-check size-4 text-n-slate-10"
                    />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isMigrating" class="flex flex-col gap-2">
        <div class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.MIGRATE.PROGRESS_TITLE') }}
        </div>
        <div
          class="relative w-full h-2.5 bg-n-alpha-3 rounded-full overflow-hidden ring-1 ring-inset ring-n-brand/20"
          role="progressbar"
          :aria-valuenow="Math.min(animatedPercent, 100)"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="absolute left-0 top-0 h-full bg-gradient-to-r from-n-brand to-n-brand/90 transition-[width] duration-700 ease-linear"
            :style="{ width: Math.min(animatedPercent, 100) + '%' }"
          />
        </div>
        <div class="text-xs text-n-slate-10">
          {{
            t('INBOX_MGMT.MIGRATE.PERCENT', {
              percent: Math.min(animatedPercent, 100),
            })
          }}
        </div>
      </div>
      <div v-else-if="isCompleted" class="flex flex-col gap-3">
        <div>
          <div class="text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.MIGRATE.COMPLETED_TITLE') }}
          </div>
          <div
            class="relative w-full h-2.5 bg-n-alpha-3 rounded-full overflow-hidden ring-1 ring-inset ring-n-brand/20"
            role="progressbar"
            aria-valuenow="100"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="absolute left-0 top-0 h-full w-full bg-gradient-to-r from-n-brand to-n-brand/90 transition-[width] duration-700 ease-linear"
            />
          </div>
          <div class="text-xs text-n-slate-10">
            {{
              t('INBOX_MGMT.MIGRATE.PERCENT', {
                percent: 100,
              })
            }}
          </div>
        </div>
        <div class="text-sm text-n-slate-12">
          {{
            t('INBOX_MGMT.MIGRATE.SUMMARY', {
              count: totals.processed_conversations || 0,
            })
          }}
        </div>
        <div class="flex justify-end gap-2">
          <Button
            ruby
            :label="t('INBOX_MGMT.MIGRATE.FINISH')"
            @click="closeModal"
          />
        </div>
      </div>
      <div v-else class="flex justify-end gap-2">
        <Button
          faded
          slate
          :label="t('INBOX_MGMT.MIGRATE.CANCEL')"
          @click="closeModal"
        />
        <Button
          ruby
          :disabled="!fromInboxId || !toInboxId || fromInboxId === toInboxId"
          :label="t('INBOX_MGMT.MIGRATE.SUBMIT')"
          @click="migrate"
        />
      </div>
    </div>
  </Modal>
</template>
